import { useQuery } from "@tanstack/react-query";
import { ksAchatsQK, ksFacturesQK, ksHistoriqueAbonnementsQK, ksProduitsQK, ksUserQK } from "../lib/query-keys";
import { findAllAchats, findAllProduits } from "../lib/queries/produits-queries";
import { findAllFactures } from "../lib/queries/factures-queries";
import Title from "../components/ui/Title";
import { Alert } from "../components/ui/alert";
import BlocFacturesAPayers from "../components/factures/BlocFacturesAPayers";
import { Skeleton } from "../components/ui/skeleton";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import ProduitItem from "../components/boutique/ProduitItem";
import { findUser } from "../lib/queries/user-queries";
import { findAllAbonnements } from "../lib/queries/abonnement-queries";
import { useMemo } from "react";

const BoutiquePage = () => {
  const [parent] = useAutoAnimate();
  const { data: user, isError: errorUser } = useQuery({
    queryKey: [ksUserQK],
    queryFn: findUser,
    refetchOnWindowFocus: false,
  });
  const { data: produits, isError: errorProduits } = useQuery({
    queryKey: [ksProduitsQK],
    queryFn: findAllProduits,
    refetchOnWindowFocus: false,
  });
  const { data: achats, isError: errorAchats } = useQuery({
    queryKey: [ksAchatsQK],
    queryFn: findAllAchats,
  });
  const { data: factures, isError: errorFactures } = useQuery({
    queryKey: [ksFacturesQK],
    queryFn: findAllFactures,
  });
  const { data: abonnements } = useQuery({
    queryKey: [ksHistoriqueAbonnementsQK],
    queryFn: findAllAbonnements,
  });

  const isProductBought = (id: string) => {
    if (!achats) return true;
    const match = achats.filter((prod) => prod.id === id);
    if (match.length > 0) return true;
    return false;
  };

  const hasFactureEnAttente = (productId: string) => {
    if (!factures) return true;
    const match = factures.filter((f) => f.produitId === productId);
    if (match.length === 0) return false;
    if (match[0].payee) return false;
    return true;
  };

  const peutAcheterEtiquettes = useMemo(() => {
    if (!abonnements) return false;
    const abonnementActif = abonnements.filter((a) => a.actif);
    if (abonnementActif.length === 0) return false;
    return abonnementActif.some((a) => a.citeo === true);
  }, [abonnements]);

  return (
    <div>
      <Title className='mb-6'>Boutique</Title>
      <BlocFacturesAPayers />

      <div className='flex flex-col gap-3' ref={parent}>
        {/* Error */}
        {(errorAchats || errorFactures || errorProduits || errorUser) && (
          <Alert variant={"destructive"}>Une erreur s'est produite. Veuillez réessayer ultérieurement.</Alert>
        )}

        {/* Loading */}
        {(!factures || !produits || !achats || !user) && (
          <>
            <Skeleton className='w-full h-40' />
            <Skeleton className='w-full h-40' />
          </>
        )}

        {/* DATA */}
        {!!achats && !!factures && !!produits && !!user && (
          <>
            {produits.map((prod) => (
              <ProduitItem
                produit={prod}
                isDisabled={
                  (!user.raisonSociale && user.personneMorale) ||
                  (!user.prenom && !user.personneMorale) ||
                  !peutAcheterEtiquettes
                }
                key={prod.id}
                isProductBought={isProductBought(prod.id)}
                hasFactureEnAttente={hasFactureEnAttente(prod.id)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default BoutiquePage;
